import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import LayersIcon from "@mui/icons-material/Layers";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../components/TextField";
import GridTable from "../../components/GridTable";
import ActionMenu from "../../components/ActionMenu";
import Dialog from "../../components/Dialog";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import { successMessage, errorMessage } from "../../components/Toaster";
import { getAllMachines, createMachine, updateMachine, deleteMachine } from "./api";
import { ApiResponse, MachineData, CreateMachineParams } from "./interface";
import { getErrorMessage } from "../../utils/error";

const Machine: React.FC = () => {
    const navigate = useNavigate();
    const [machines, setMachines] = useState<MachineData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editMachine, setEditMachine] = useState<MachineData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        machineName: Yup.string().required("Machine name is required"),
        machineCode: Yup.string().required("Machine Code is required"),
    });

    const columns: GridColDef[] = [
        { field: "machine_name", headerName: "Machine Name", flex: 1 },
        { field: "machine_code", headerName: "Machine Code", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                        {
                            icon: <LayersIcon />,
                            tooltip: "Version",
                            onClick: handleViewVersions,
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchMachines = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<MachineData[]> = await getAllMachines();
                if (res.status && res.data) {
                    setMachines(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch machines.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching machines");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchMachines();
    }, []);

    const openDialog = () => setDialogOpen(true);
    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditMachine(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditMachine(params.row as MachineData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleViewVersions = (params: GridCellParams) => {
        const machine_id = params.row.machine_id;
        const machine_name = params.row.machine_name;
        navigate(`/machines/${machine_id}/versions`, {
            state: { machine_name }
        });
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editMachine) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newMachine: CreateMachineParams = {
                machine_name: values.machineName.trim(),
                machine_code: values.machineCode.trim(),
            };

            const response = await createMachine(newMachine);
            if (response.status) {
                const machine = {
                    machine_id: response.data!.machine_id,
                    machine_name: values.machineName,
                    machine_code: values.machineCode
                };
                setMachines(prev => [...prev, machine]);
                successMessage(response.message);
                return true;
            } else {
                errorMessage(response.message || "Failed to create machine.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading(prev => ({ ...prev, update: true }));
        try {
            const updatedData = {
                machine_name: values.machineName.trim(),
                machine_code: values.machineCode.trim(),
                machine_id: editMachine?.machine_id || "",
            };
            const response = await updateMachine(updatedData);
            if (response.status) {
                setMachines(prev =>
                    prev.map(machine =>
                        machine.machine_id === editMachine?.machine_id
                            ? { ...machine, ...updatedData }
                            : machine
                    )
                );
                successMessage(response.message || "Machine updated successfully!");
                return true;
            } else {
                errorMessage(response.message || "Failed to update machine.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const response = await deleteMachine(itemToDelete.row.machine_id);
                if (response.status) {
                    setMachines(prev => prev.filter(m => m.machine_id !== itemToDelete.row.machine_id));
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || "Failed to delete machine.");
                }
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                            <GridTable
                                rows={machines}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.machine_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                machineName: editMachine?.machine_name || "",
                                machineCode: editMachine?.machine_code || "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={editMachine ? "Edit Machine" : "Add New Machine"}
                                    confirmButtonText={editMachine ? "Update" : "Add"}
                                    cancelButtonText="Cancel"
                                    icon={<MiscellaneousServicesIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.update || loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={6}>
                                                            <TextField
                                                                id="machineName"
                                                                name="machineName"
                                                                label="Machine Name"
                                                                variant="outlined"
                                                                fullWidth
                                                                value={values.machineName}
                                                                onChange={handleChange}
                                                                required
                                                                error={Boolean(touched.machineName && errors.machineName)}
                                                                helperText={touched.machineName && errors.machineName}
                                                            />
                                                        </Grid>
                                                        <Grid size={6}>
                                                            <TextField
                                                                id="machineCode"
                                                                name="machineCode"
                                                                label="Machine Code"
                                                                variant="outlined"
                                                                fullWidth
                                                                value={values.machineCode}
                                                                onChange={handleChange}
                                                                required
                                                                error={Boolean(touched.machineCode && errors.machineCode)}
                                                                helperText={touched.machineCode && errors.machineCode}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                    </main>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Delete Machine" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this machine?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Machine;
